import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * A NATIVE `<select>` wearing the shared control recipe.
 *
 * <p>This is deliberately not `SimpleSelect`. `SimpleSelect` is a Radix listbox — a different DOM, a
 * different keyboard model, a portalled popover and its own churn guard — and swapping a native
 * `<select>` for it is a behaviour change, not a restyle. Nine hand-rolled `<select>`s and one
 * `<textarea>` (FU-F2) needed the *edge*, not a new widget: on a phone the native picker is also the
 * better control, so the fix is to give the native element the measured boundary rather than to
 * replace it.
 *
 * <p>The class list is `input.tsx`'s, with three deliberate differences and no others:
 * <ul>
 *   <li>no `file:` utilities — a select has no file-selector pseudo-element;</li>
 *   <li>`appearance-none` is NOT set, so the platform keeps drawing its own disclosure arrow. A
 *       select with the arrow suppressed and nothing put back is unreadable as a select;</li>
 *   <li>`pr-8` leaves room for that arrow so a long option label does not run under it.</li>
 * </ul>
 *
 * <p>Everything load-bearing is shared with `Input`/`Textarea`: `border-control-boundary` (the
 * MEASURED 3:1 boundary token, pinned by `scripts/check-control-boundary-tokens.mjs` check A),
 * `bg-input/70` as the fill, `rounded-lg`, and the one focus ring the design language allows
 * (`focus-visible:border-ring` + `ring-[3px] ring-ring/50`). The 44px coarse-pointer floor arrives by
 * the `min-h` path for the reason `input.tsx` documents at length: gated on the INPUT DEVICE, never on
 * a viewport breakpoint.
 *
 * <p>Guard: check D of `scripts/check-control-boundary-tokens.mjs` fails the build for any native
 * `<input>`/`<select>`/`<textarea>` that paints a colourless `border` — which is what all ten sites
 * were doing, since Tailwind v4's preflight is `border: 0 solid` with no colour and a bare `border`
 * utility therefore resolves to `currentColor`.
 */
const NativeSelect = React.forwardRef<HTMLSelectElement, React.ComponentProps<"select">>(
  function NativeSelect({ className, ...props }, ref) {
    return (
      <select
        ref={ref}
        data-slot="native-select"
        className={cn(
          "h-9 touch-manipulation [@media(pointer:coarse)]:min-h-11 w-full min-w-0 rounded-lg border border-control-boundary bg-input/70 px-3 pr-8 py-1 text-base transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
          "aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
          className
        )}
        {...props}
      />
    )
  }
)

NativeSelect.displayName = "NativeSelect"

export { NativeSelect }
